/**
 * WhatsAppOAuthDialog - Dialog para conexão OAuth com WhatsApp Business via NotificaMe
 * 
 * Este componente abre um popup com a URL OAuth do Facebook para que o cliente
 * possa conectar sua conta Business Manager sem acessar o painel do NotificaMe.
 * 
 * Fluxo:
 * 1. Cliente clica em "Conectar WhatsApp Business" no InboxCard
 * 2. Este dialog exibe instruções e abre popup com URL OAuth
 * 3. Cliente faz login no Facebook e autoriza
 * 4. Popup fecha (redirect volta pro NotificaMe)
 * 5. Dialog atualiza status da inbox para "pending_setup"
 * 
 * @release v1.0 - 2025-01-28
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  MessageCircle, 
  ExternalLink, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';

/**
 * Props do componente WhatsAppOAuthDialog
 */
interface WhatsAppOAuthDialogProps {
  /** Controla se o dialog está aberto */
  open: boolean;
  /** Callback para alterar estado de abertura do dialog */
  onOpenChange: (open: boolean) => void;
  /** ID da inbox a ser conectada */
  inboxId: string;
  /** Nome da inbox para exibição */
  inboxName: string;
  /** URL OAuth do Facebook (obtida das credenciais da instância) */
  oauthUrl: string;
  /** Callback chamado quando OAuth é concluído com sucesso */
  onSuccess: () => void;
}

/**
 * Estados possíveis do processo de conexão OAuth
 */
type ConnectionState = 'idle' | 'connecting' | 'success' | 'error';

/**
 * Componente de dialog para conexão OAuth WhatsApp Business
 * Abre popup do Facebook e monitora quando é fechado
 */
export function WhatsAppOAuthDialog({
  open,
  onOpenChange,
  inboxId,
  inboxName,
  oauthUrl,
  onSuccess,
}: WhatsAppOAuthDialogProps) {
  // Estado da conexão OAuth
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  
  // Referência ao popup aberto
  const popupRef = useRef<Window | null>(null);
  
  // Intervalo de polling para verificar se popup fechou
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Limpa o intervalo de polling quando componente desmonta ou dialog fecha
   */
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  /**
   * Reseta estado quando dialog fecha
   */
  useEffect(() => {
    if (!open) {
      setConnectionState('idle');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [open]);

  /**
   * Abre popup com URL OAuth e inicia polling para detectar fechamento
   */
  const handleOpenOAuthPopup = () => {
    if (!oauthUrl) {
      logger.error('WhatsAppOAuthDialog', 'URL OAuth não configurada');
      setConnectionState('error');
      return;
    }

    logger.info('WhatsAppOAuthDialog', 'Abrindo popup OAuth', { inboxId, inboxName });
    setConnectionState('connecting');

    // Configurações do popup - centralizado na tela
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popupFeatures = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,scrollbars=yes`;

    // Abre popup com URL OAuth
    popupRef.current = window.open(oauthUrl, 'whatsapp_oauth_popup', popupFeatures);

    if (!popupRef.current) {
      // Popup bloqueado pelo navegador
      logger.warn('WhatsAppOAuthDialog', 'Popup bloqueado pelo navegador');
      setConnectionState('error');
      return;
    }

    // Inicia polling para verificar se popup foi fechado
    pollingIntervalRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        logger.info('WhatsAppOAuthDialog', 'Popup fechado pelo usuário');
        
        // Limpa intervalo
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Assume sucesso quando popup fecha (OAuth concluído no NotificaMe)
        setConnectionState('success');
      }
    }, 1000); // Verifica a cada 1 segundo
  };

  /**
   * Confirma que OAuth foi concluído e chama callback de sucesso
   */
  const handleConfirmSuccess = () => {
    logger.info('WhatsAppOAuthDialog', 'Usuário confirmou conclusão do OAuth', { inboxId });
    onSuccess();
  };

  /**
   * Fecha dialog sem confirmar
   */
  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-500/10">
              <MessageCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            Conectar WhatsApp Business
          </DialogTitle>
          <DialogDescription>
            Conecte sua conta do WhatsApp Business à inbox "{inboxName}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Estado: Aguardando início */}
          {connectionState === 'idle' && (
            <>
              {/* Instruções */}
              <div className="space-y-3 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Para conectar sua conta:</p>
                <ol className="list-decimal list-inside space-y-2 ml-1">
                  <li>Clique no botão abaixo para abrir a conexão</li>
                  <li>Faça login na sua conta do Facebook Business</li>
                  <li>Selecione o número do WhatsApp Business</li>
                  <li>Autorize o acesso</li>
                  <li>Feche a janela quando terminar</li>
                </ol>
              </div>

              {/* Aviso de requisitos */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 dark:text-amber-300">
                  <p className="font-medium">Requisitos:</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>Conta Meta Business Suite ativa</li>
                    <li>Número WhatsApp Business verificado</li>
                  </ul>
                </div>
              </div>

              {/* Botão para abrir popup */}
              <Button 
                onClick={handleOpenOAuthPopup}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir Conexão WhatsApp
              </Button>
            </>
          )}

          {/* Estado: Conectando (popup aberto) */}
          {connectionState === 'connecting' && (
            <div className="text-center space-y-4 py-4">
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-green-600" />
              </div>
              <div className="space-y-1">
                <p className="font-medium">Aguardando conexão...</p>
                <p className="text-sm text-muted-foreground">
                  Complete o login na janela que foi aberta.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                A janela fechará automaticamente quando você concluir.
              </p>
            </div>
          )}

          {/* Estado: Sucesso (popup fechado) */}
          {connectionState === 'success' && (
            <div className="space-y-4">
              <div className="text-center space-y-3 py-2">
                <div className="flex justify-center">
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-green-600 dark:text-green-400">
                    Conexão iniciada!
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Clique em confirmar para finalizar o processo.
                  </p>
                </div>
              </div>

              {/* Info sobre ativação */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Após autorização, nossa equipe finalizará a ativação em até 24 horas úteis.
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleConfirmSuccess}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirmar
                </Button>
              </div>
            </div>
          )}

          {/* Estado: Erro */}
          {connectionState === 'error' && (
            <div className="text-center space-y-4 py-4">
              <div className="flex justify-center">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="font-medium text-destructive">
                  Erro ao abrir conexão
                </p>
                <p className="text-sm text-muted-foreground">
                  O popup pode ter sido bloqueado pelo navegador. 
                  Permita popups e tente novamente.
                </p>
              </div>
              <Button 
                onClick={() => setConnectionState('idle')}
                variant="outline"
              >
                Tentar Novamente
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
